import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, MapPin, Search, Warehouse, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, newIdempotencyKey } from "@/lib/api";
import { LOCATION_TYPE_LABELS } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * Adressefelt med forslag frå lagra stader og Google Places (via backend).
 * value: { name, address, place_id, lat, lng, location_id } | null
 * Fritekst er alltid lov – då blir adressa lagra som manuell (utan koordinatar).
 */
export const AddressInput = ({ value, onChange, placeholder = "Søk adresse eller stad", locations = [], mapsAvailable = true, disabled = false, autoFocus = false, testId = "address-input" }) => {
  const [query, setQuery] = useState(value?.address || value?.name || "");
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const sessionRef = useRef(newIdempotencyKey());
  const requestRef = useRef(0);
  const rootRef = useRef(null);

  // Hald tekstfeltet i takt når verdien blir sett utanfrå (t.d. redigering av eksisterande ordre).
  useEffect(() => {
    const text = value?.address || value?.name || "";
    setQuery((q) => (q === text ? q : text));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.address, value?.name, value?.place_id]);

  useEffect(() => {
    if (!open || !mapsAvailable) return undefined;
    const text = query.trim();
    if (text.length < 2 || (value?.place_id && text === (value.address || value.name))) {
      setSuggestions([]);
      return undefined;
    }
    const id = ++requestRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.post("/maps/autocomplete", { input: text, session_token: sessionRef.current });
        if (id === requestRef.current) setSuggestions(res.data.suggestions || []);
      } catch {
        if (id === requestRef.current) setSuggestions([]);
      } finally {
        if (id === requestRef.current) setLoading(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [query, open, mapsAvailable, value?.place_id, value?.address, value?.name]);

  const savedMatches = useMemo(() => {
    const text = query.trim().toLowerCase();
    const list = locations.filter((l) => l.active !== false);
    if (!text) return list.slice(0, 6);
    return list.filter((l) => `${l.name} ${l.address || ""} ${LOCATION_TYPE_LABELS[l.type] || ""}`.toLowerCase().includes(text)).slice(0, 6);
  }, [locations, query]);

  const options = useMemo(() => {
    const out = savedMatches.map((l) => ({ kind: "saved", key: `saved-${l.id}`, location: l }));
    suggestions.forEach((s) => out.push({ kind: "google", key: `g-${s.place_id}`, suggestion: s }));
    const text = query.trim();
    if (text && !value?.place_id) out.push({ kind: "manual", key: "manual", text });
    return out;
  }, [savedMatches, suggestions, query, value?.place_id]);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  const pick = async (opt) => {
    if (opt.kind === "saved") {
      const l = opt.location;
      onChange({ name: l.name, address: l.address || l.name, place_id: l.place_id || null, lat: l.lat ?? null, lng: l.lng ?? null, location_id: l.id });
      setQuery(l.address || l.name);
    } else if (opt.kind === "google") {
      const s = opt.suggestion;
      setQuery(s.text);
      onChange({ name: s.main_text, address: s.text, place_id: s.place_id, lat: null, lng: null, location_id: null });
      try {
        const res = await api.get(`/maps/place/${s.place_id}`, { params: { session_token: sessionRef.current } });
        const d = res.data;
        onChange({ name: d.name || s.main_text, address: d.address || s.text, place_id: d.place_id || s.place_id, lat: d.lat ?? null, lng: d.lng ?? null, location_id: null });
        setQuery(d.address || s.text);
      } catch {
        /* Behald forslaget utan koordinatar – ruta brukar place_id. */
      }
      sessionRef.current = newIdempotencyKey();
    } else {
      onChange({ name: null, address: opt.text, place_id: null, lat: null, lng: null, location_id: null });
    }
    close();
  };

  const handleInput = (text) => {
    setQuery(text);
    setOpen(true);
    setActive(-1);
    if (!text.trim()) onChange(null);
    else onChange({ name: null, address: text, place_id: null, lat: null, lng: null, location_id: null });
  };

  const onKeyDown = (e) => {
    if (!open && ["ArrowDown", "ArrowUp"].includes(e.key)) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (active >= 0 && options[active]) {
        e.preventDefault();
        pick(options[active]);
      } else {
        close();
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  const confirmed = Boolean(value?.place_id || value?.location_id);

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(close, 120)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className={cn("h-11 bg-card pl-9 pr-9 md:h-10", confirmed && "pr-16")}
          data-testid={`${testId}-field`}
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Søkjer" /> : null}
          {confirmed && !loading ? <Check className="h-4 w-4 text-success" aria-label="Stadfesta adresse" data-testid={`${testId}-confirmed`} /> : null}
          {query ? (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setQuery("");
                onChange(null);
                setSuggestions([]);
              }}
              className="focus-ring flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Tøm"
              data-testid={`${testId}-clear`}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {open && options.length > 0 ? (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-dropdown mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-popover py-1 shadow-sm"
          data-testid={`${testId}-listbox`}
        >
          {options.map((opt, i) => {
            const isActive = i === active;
            if (opt.kind === "saved") {
              const l = opt.location;
              return (
                <li key={opt.key} role="option" aria-selected={isActive}>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(opt)} className={cn("flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-muted", isActive && "bg-muted")} data-testid={`${testId}-option-saved`}>
                    <Warehouse className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{l.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">{LOCATION_TYPE_LABELS[l.type] || l.type}{l.address ? ` · ${l.address}` : ""}</span>
                    </span>
                  </button>
                </li>
              );
            }
            if (opt.kind === "google") {
              const s = opt.suggestion;
              return (
                <li key={opt.key} role="option" aria-selected={isActive}>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(opt)} className={cn("flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-muted", isActive && "bg-muted")} data-testid={`${testId}-option-google`}>
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{s.main_text || s.text}</span>
                      {s.secondary_text ? <span className="block truncate text-xs text-muted-foreground">{s.secondary_text}</span> : null}
                    </span>
                  </button>
                </li>
              );
            }
            return (
              <li key={opt.key} role="option" aria-selected={isActive} className="border-t border-border">
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(opt)} className={cn("flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted", isActive && "bg-muted")} data-testid={`${testId}-option-manual`}>
                  <span className="text-muted-foreground">Bruk som skriven:</span> <span className="truncate font-medium">«{opt.text}»</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};
